import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DeleteOutlined,
  FormOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import axios from "axios";

const SupplierList = ({
  searchKeyword = "",
  sortBy = "name",
  sortOrder = "asc",
  currentPage = 1,
  onPageChange,
}) => {
  const [totalBrands, setTotalBrands] = useState(0);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pageSize = 8;
  const [deleteId, setDeleteId] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    fetchData();
  }, [currentPage, searchKeyword, sortBy, sortOrder]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Build query parameters for the API
      const params = new URLSearchParams();
      params.set("page", currentPage.toString());
      params.set("perPage", pageSize.toString());

      if (searchKeyword.trim()) {
        params.set("search", searchKeyword.trim());
      }

      if (sortBy) {
        params.set("sortBy", sortBy);
      }

      if (sortOrder) {
        params.set("sortOrder", sortOrder);
      }

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/brands/getBrandpage?${params.toString()}`
      );

      if (response.status === 200) {
        const data = response.data;
        setBrands(data.brands || []);
        setTotalBrands(data.totalItems || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setBrands([]);
      setTotalBrands(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const totalPages = Math.ceil(totalBrands / pageSize);

  const handleDeleteItem = async () => {
    setDeletingId(deleteId);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/brands/deletebrand/${deleteId}`
      );
      setShowDeleteModal(false);
      fetchData(); // Refresh current page
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setDeletingId("");
    }
  };

  const handleClickDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const getVisiblePages = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const showPagination = totalPages > 1;

  return (
    <div>
      {/* Results Info */}
      {!isLoading && (
        <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
          {brands.length > 0 ? (
            <>
              Hiển thị {(currentPage - 1) * pageSize + 1} -{" "}
              {Math.min(currentPage * pageSize, totalBrands)} trong tổng số{" "}
              {totalBrands} nhà cung cấp
              {searchKeyword && (
                <span className="ml-2 text-blue-600">
                  (tìm kiếm: "{searchKeyword}")
                </span>
              )}
            </>
          ) : searchKeyword ? (
            `Không tìm thấy nhà cung cấp phù hợp với "${searchKeyword}"`
          ) : (
            "Không có nhà cung cấp nào"
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            <span>Đang tải dữ liệu...</span>
          </div>
        </div>
      )}

      {/* Table Section */}
      {!isLoading && (
        <>
          {brands.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      STT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mã NCC
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tên nhà cung cấp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Logo thương hiệu
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand, index) => (
                    <tr key={brand.idBrand} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {brand.idBrand}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="font-medium">{brand.name}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center justify-center">
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${brand.urlImage}`}
                            alt={`Logo ${brand.name}`}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm"
                            onError={(e) => {
                              e.target.src =
                                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNGM0Y0RjYiLz48cGF0aCBkPSJNMjEgMjFIMjNWMjNIMjFWMjFaTTQxIDIxSDQzVjIzSDQxVjIxWk0yNyAzOUgyOVY0MUgyN1YzOVpNMzEgMzlIMzNWNDFIMzFWMzlaIiBmaWxsPSIjOUIxMDFCIi8+PC9zdmc+";
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <Link
                            to={`/admin/supplier/edit?id=${brand.idBrand}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Chỉnh sửa"
                          >
                            <FormOutlined className="text-lg" />
                          </Link>
                          <button
                            onClick={() => handleClickDelete(brand.idBrand)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Xóa"
                            disabled={deletingId === brand.idBrand}
                          >
                            {deletingId === brand.idBrand ? (
                              <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                            ) : (
                              <DeleteOutlined className="text-lg" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🏢</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchKeyword
                  ? "Không tìm thấy nhà cung cấp"
                  : "Không có nhà cung cấp nào"}
              </h3>
              <p className="text-gray-500">
                {searchKeyword
                  ? `Không có nhà cung cấp nào phù hợp với từ khóa "${searchKeyword}"`
                  : "Chưa có nhà cung cấp nào trong hệ thống"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {showPagination && !isLoading && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            Trang {currentPage} / {totalPages} ({totalBrands} nhà cung cấp)
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            {currentPage > 1 && (
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <LeftOutlined className="mr-1" />
                Trước
              </button>
            )}

            {/* First page */}
            {currentPage > 3 && (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  1
                </button>
                {currentPage > 4 && (
                  <span className="px-2 text-gray-500">...</span>
                )}
              </>
            )}

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === page
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <span className="px-2 text-gray-500">...</span>
                )}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next Button */}
            {currentPage < totalPages && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                Sau
                <RightOutlined className="ml-1" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Xóa nhà cung cấp
            </h3>
            <p className="text-gray-600 mb-6">
              Bạn có chắc chắn muốn xóa nhà cung cấp này không? Hành động này
              không thể hoàn tác.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={deletingId}
              >
                Hủy
              </button>
              <button
                onClick={handleDeleteItem}
                disabled={deletingId}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deletingId && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierList;
